import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  contact_type: string;
  source: string | null;
  services_received: string[];
  lifetime_value: number;
  cross_sell_status: Record<string, string | null>;
  do_not_contact: boolean;
  tags: string[];
  last_contacted_at: string | null;
  last_job_closed_at: string | null;
  created_at: string;
}

interface Segments {
  roofing_no_driveway: number;
  cold_prospects: number;
  high_value: number;
  multi_service: number;
}

const SERVICE_META: Record<string, { label: string; color: string; icon: string }> = {
  roofing:   { label: 'Roofing',   color: '#4A7EB5', icon: '🏠' },
  insurance: { label: 'Insurance', color: '#06B6D4', icon: '🛡️' },
  asphalt:   { label: 'Asphalt',   color: '#F59E0B', icon: '🛣️' },
  concrete:  { label: 'Concrete',  color: '#8B5CF6', icon: '🪨' },
  hiring:    { label: 'Hiring',    color: '#EF4444', icon: '💼' },
};

const TYPE_META: Record<string, { label: string; color: string }> = {
  customer:   { label: 'Customer',   color: '#10B981' },
  prospect:   { label: 'Prospect',   color: '#4A7EB5' },
  cold_lead:  { label: 'Cold Lead',  color: '#6B7280' },
  applicant:  { label: 'Applicant',  color: '#8B5CF6' },
  referral:   { label: 'Referral',   color: '#F59E0B' },
};

const SEGMENT_CHIPS = [
  { key: 'roofing_no_driveway',   label: '🏠 Roofing → Driveway',   desc: 'Roofing customers with no driveway work yet' },
  { key: 'cold_prospects',        label: '❄️ Cold Prospects',        desc: 'Leads gone quiet for 45+ days' },
  { key: 'high_value',            label: '💎 High Value',            desc: 'Lifetime value $10k+' },
  { key: 'multi_service',         label: '🔄 Multi-Service',         desc: 'Bought 2+ service types' },
];

const timeAgo = (iso: string | null) => {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [segments, setSegments] = useState<Segments | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterService, setFilterService] = useState('');
  const [activeSegment, setActiveSegment] = useState('');
  const [page, setPage] = useState(1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportChannel, setExportChannel] = useState<'meta' | 'google' | 'email'>('meta');
  const [exporting, setExporting] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '50' };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (filterService) params.service = filterService;
      if (activeSegment) params.segment = activeSegment;
      const { data } = await api.get('/contacts', { params });
      setContacts(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterService, activeSegment, page]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  useEffect(() => {
    api.get('/contacts/segments').then(r => setSegments(r.data)).catch(() => {});
  }, []);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filterType, filterService, activeSegment]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const body: Record<string, string> = { channel: exportChannel };
      if (activeSegment) body.segment = activeSegment;
      const { data } = await api.post('/contacts/export', body);

      // Download as JSON or CSV
      let content: string;
      let filename: string;
      let mime: string;

      if (exportChannel === 'email') {
        content = ['name,email', ...data.records.map((r: any) => `${r.name},${r.email}`)].join('\n');
        filename = 'contacts-email-list.csv';
        mime = 'text/csv';
      } else {
        content = JSON.stringify(data.records, null, 2);
        filename = `contacts-${exportChannel}-audience.json`;
        mime = 'application/json';
      }

      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div style={{ padding: 24, color: '#f1f5f9', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>👤 Contacts</h1>
          <span style={{
            background: '#1A2B3C', border: '1px solid #2D4259',
            borderRadius: 20, padding: '2px 12px', fontSize: 13, color: '#8FA8C0',
          }}>{total.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setShowExportModal(true)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid #2D4259',
              background: '#1A2B3C', color: '#8FA8C0', fontSize: 13, cursor: 'pointer',
            }}
          >📤 Export</button>
          <button
            onClick={() => navigate('/contacts/new')}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#4A7EB5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >+ Add Contact</button>
        </div>
      </div>

      {/* Smart Segment Chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {SEGMENT_CHIPS.map(chip => {
          const count = segments ? (segments as any)[chip.key] : null;
          const isActive = activeSegment === chip.key;
          return (
            <button
              key={chip.key}
              title={chip.desc}
              onClick={() => setActiveSegment(isActive ? '' : chip.key)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: isActive ? '1.5px solid #4A7EB5' : '1px solid #2D4259',
                background: isActive ? 'rgba(74,126,181,0.15)' : '#1A2B3C',
                color: isActive ? '#4A7EB5' : '#8FA8C0',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {chip.label}
              {count !== null && (
                <span style={{
                  background: isActive ? '#4A7EB5' : '#2D4259',
                  color: isActive ? '#fff' : '#8FA8C0',
                  borderRadius: 10, padding: '1px 7px', fontSize: 11,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, phone..."
          style={{
            flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8,
            border: '1px solid #2D4259', background: '#1A2B3C', color: '#f1f5f9', fontSize: 13,
          }}
        />
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #2D4259',
            background: '#1A2B3C', color: '#f1f5f9', fontSize: 13,
          }}
        >
          <option value="">All Types</option>
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Service quick-filter buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[{ key: '', label: '🔍 All', color: '#4A7EB5' }, ...Object.entries(SERVICE_META).filter(([k]) => k !== 'hiring').map(([k, v]) => ({ key: k, label: `${v.icon} ${v.label}`, color: v.color }))].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setFilterService(key)}
            style={{
              padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: filterService === key ? `1.5px solid ${color}` : '1px solid #2D4259',
              background: filterService === key ? `${color}22` : '#1A2B3C',
              color: filterService === key ? color : '#8FA8C0',
              transition: 'all 0.15s',
            }}
          >{label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#1A2B3C', borderRadius: 12, border: '1px solid #2D4259', overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 120px 180px 100px 110px 110px 48px',
          padding: '10px 16px', borderBottom: '1px solid #2D4259',
          fontSize: 11, color: '#8FA8C0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          <span>Name</span>
          <span>Type</span>
          <span style={{ paddingLeft: 16 }}>Services</span>
          <span>Source</span>
          <span>Lifetime $</span>
          <span>Last Activity</span>
          <span />
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8FA8C0' }}>Loading...</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8FA8C0' }}>
            No contacts found.{activeSegment ? ' Try clearing the segment filter.' : ''}
          </div>
        ) : (
          contacts.map((contact, i) => {
            const typeMeta = TYPE_META[contact.contact_type] || { label: contact.contact_type, color: '#6B7280' };
            const lastActivity = contact.last_job_closed_at || contact.last_contacted_at || contact.created_at;
            return (
              <div
                key={contact.id}
                onClick={() => navigate(`/contacts/${contact.id}`)}
                style={{
                  display: 'grid', gridTemplateColumns: '2fr 120px 180px 100px 110px 110px 48px',
                  padding: '12px 16px', alignItems: 'center',
                  borderBottom: i < contacts.length - 1 ? '1px solid #1e3448' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(74,126,181,0.06)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                {/* Name */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>
                    {contact.do_not_contact && <span title="Do Not Contact" style={{ marginRight: 6, fontSize: 12 }}>🚫</span>}
                    {contact.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#8FA8C0', marginTop: 1 }}>
                    {contact.email || contact.phone || contact.address || '—'}
                  </div>
                </div>

                {/* Type */}
                <span style={{
                  background: typeMeta.color + '22', color: typeMeta.color,
                  borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700,
                  display: 'inline-block',
                }}>
                  {typeMeta.label}
                </span>

                {/* Services */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingLeft: 16 }}>
                  {(contact.services_received || []).length === 0
                    ? <span style={{ color: '#4A6580', fontSize: 12 }}>None</span>
                    : (contact.services_received || []).map(s => {
                        const sm = SERVICE_META[s];
                        if (!sm) return null;
                        return (
                          <span key={s} style={{
                            background: sm.color + '22', color: sm.color,
                            borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 600,
                          }}>{sm.icon} {sm.label}</span>
                        );
                      })
                  }
                </div>

                {/* Source */}
                <span style={{ fontSize: 12, color: '#8FA8C0', textTransform: 'capitalize' }}>
                  {contact.source || '—'}
                </span>

                {/* Lifetime value */}
                <span style={{ fontSize: 13, fontWeight: 600, color: contact.lifetime_value > 0 ? '#10B981' : '#4A6580' }}>
                  {contact.lifetime_value > 0 ? `$${Number(contact.lifetime_value).toLocaleString()}` : '—'}
                </span>

                {/* Last activity */}
                <span style={{ fontSize: 12, color: '#8FA8C0' }}>
                  {timeAgo(lastActivity)}
                </span>

                {/* Arrow */}
                <span style={{ color: '#4A6580', fontSize: 16, textAlign: 'center' }}>›</span>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid #2D4259',
              background: '#1A2B3C', color: page === 1 ? '#4A6580' : '#f1f5f9', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
            }}
          >← Prev</button>
          <span style={{ padding: '6px 14px', color: '#8FA8C0', fontSize: 13 }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid #2D4259',
              background: '#1A2B3C', color: page === totalPages ? '#4A6580' : '#f1f5f9',
              cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13,
            }}
          >Next →</button>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#1A2B3C', borderRadius: 16, padding: 28, width: 400,
            border: '1px solid #2D4259', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800 }}>📤 Export Contacts</h3>
            <p style={{ color: '#8FA8C0', fontSize: 13, margin: '0 0 20px' }}>
              {activeSegment
                ? `Exporting segment: ${SEGMENT_CHIPS.find(c => c.key === activeSegment)?.label}`
                : `Exporting all ${total.toLocaleString()} contacts`}
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: '#8FA8C0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
                Channel
              </label>
              {[
                { key: 'meta',   label: 'Meta Custom Audience', desc: 'Facebook & Instagram ads — hashed email + phone' },
                { key: 'google', label: 'Google Customer Match',  desc: 'Google Search, Display & YouTube — hashed' },
                { key: 'email',  label: 'Email List',             desc: 'Plain email addresses (CSV)' },
              ].map(opt => (
                <div
                  key={opt.key}
                  onClick={() => setExportChannel(opt.key as any)}
                  style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 6,
                    border: exportChannel === opt.key ? '1.5px solid #4A7EB5' : '1px solid #2D4259',
                    background: exportChannel === opt.key ? 'rgba(74,126,181,0.1)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: '#8FA8C0', marginTop: 2 }}>{opt.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #2D4259',
                  background: 'transparent', color: '#8FA8C0', cursor: 'pointer', fontSize: 13,
                }}
              >Cancel</button>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={{
                  flex: 1, padding: '10px', borderRadius: 8, border: 'none',
                  background: '#4A7EB5', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13,
                }}
              >{exporting ? 'Exporting...' : 'Export'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
